import { useEffect, useMemo, useRef, useState } from 'react'
import {
  GeoJSONSource, LngLatBounds, Map as MapLibreMap, MapMouseEvent, NavigationControl,
  type CircleLayerSpecification,
} from 'maplibre-gl'
import { baseStyle, opening } from './LiveMap'
import { translate, type Lang } from './i18n'
import { DataQuality } from './DataQuality'
import { IncidentExplorer } from './IncidentExplorer'
import { Tabs } from './Tabs'
import { SetChangesPanel } from './SetChanges'
import { DataNote } from './DataNote'
import { useAtlasWindows } from './shifts'
import type {
  HistoricalIncident, HotspotSet, HotspotSetDetail, IncidentFilter, IncidentPage,
  IngestionRun, QualityReport, SetChanges, Source,
  Denial, MapArea, MapBase } from './api'
import { DEFAULT_MAP_BASE } from './api'
import { fmtDateTime } from './time'

/**
 * El mapa del analista: dónde está el delito, no dónde están las patrullas.
 *
 * El radio dice el riesgo y el color la prioridad. Es la misma escala en todos los conjuntos, así
 * que dos conjuntos se ven distintos porque lo son, no porque el mapa los haya reescalado.
 */
/**
 * Las capas del mapa del analista, de abajo arriba. Son datos: `analyst-map.test` las valida contra
 * la especificación de MapLibre, porque una expresión mal escrita tumba la capa sin avisar (así
 * pasó con el radio de los incidentes, que tenía `zoom` dentro de un `case`).
 */
export const AnalystLayers: CircleLayerSpecification[] = [
  // Los incidentes van debajo de los hotspots y en pequeño: son el detrás, no el resultado. El
  // color dice si la fila sirve, que es lo que el analista viene a mirar en esta pantalla. Colores
  // claros y un borde fino: con el mapa oscuro de la agencia, el gris azulado de antes se perdía.
  {
    id: 'incidentes',
    type: 'circle',
    source: 'incidentes',
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 2.4, 15, 5],
      'circle-color': [
        'match', ['get', 'status'],
        'Unusable', '#e5484d',
        'Review', '#f5a524',
        '#4ea8f5',
      ],
      'circle-opacity': 0.85,
      'circle-stroke-width': 0.6,
      'circle-stroke-color': '#ffffff',
    },
  },
  {
    id: 'hotspots',
    type: 'circle',
    source: 'hotspots',
    paint: {
      // El riesgo es 0–100 y relativo al conjunto: se traduce a radio para poder comparar de un
      // vistazo sin leer la tabla.
      'circle-radius': ['interpolate', ['linear'], ['get', 'risk'], 0, 5, 100, 18],
      'circle-color': [
        'match', ['get', 'priority'],
        'High', '#b3261e',
        'Medium', '#e8a33d',
        '#1f5fa8',
      ],
      'circle-opacity': 0.55,
      'circle-stroke-width': ['case', ['==', ['get', 'selected'], true], 3, 1],
      'circle-stroke-color': '#ffffff',
    },
  },
  // El incidente elegido en la lista, encima de todo: un hotspot no lo puede tapar.
  {
    id: 'incidente-elegido',
    type: 'circle',
    source: 'incidentes',
    filter: ['==', ['get', 'selected'], true],
    paint: {
      'circle-radius': 9,
      'circle-color': [
        'match', ['get', 'status'],
        'Unusable', '#e5484d',
        'Review', '#f5a524',
        '#4ea8f5',
      ],
      'circle-stroke-width': 3,
      'circle-stroke-color': '#ffffff',
    },
  },
]

/**
 * Agrega la fuente y la capa de hotspots.
 *
 * Va aparte porque <c>setStyle</c> reemplaza el estilo entero y se lleva por delante todo lo que
 * uno haya agregado: al cambiar de tema hay que volver a ponerlo.
 */
function addAnalystLayers(m: MapLibreMap, onSelect: (id: string | null) => void) {
  if (m.getSource('hotspots')) return

  m.addSource('incidentes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  m.addSource('hotspots', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  for (const layer of AnalystLayers) m.addLayer(layer)
  m.on('click', 'hotspots', (e: MapMouseEvent & { features?: { properties: Record<string, unknown> }[] }) => {
    // Lo que trae el mapa es `unknown`: si no es un identificador, no se elige nada.
    const id = e.features?.[0]?.properties?.['id']
    onSelect(typeof id === 'string' ? id : '')
  })
  m.on('mouseenter', 'hotspots', () => { m.getCanvas().style.cursor = 'pointer' })
  m.on('mouseleave', 'hotspots', () => { m.getCanvas().style.cursor = '' })
}

function HotspotMap({ detail, incidents, dark, selected, onSelect, base, area, background, selectedIncident = null }: {
  /** El fondo de la agencia para el mapa oscuro (§16.3d). */
  background?: string
  /** El incidente elegido en el explorador: se resalta y el mapa va a él. */
  selectedIncident?: string | null
  detail: HotspotSetDetail | null
  incidents: HistoricalIncident[]
  dark: boolean
  selected: string | null
  onSelect: (hotspotId: string | null) => void
  base: MapBase
  area: MapArea | null
}) {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const [ready, setReady] = useState(0)
  const encuadrado = useRef<string | null>(null)

  useEffect(() => {
    if (map.current || !container.current) return
    const m = new MapLibreMap({
      container: container.current,
      style: baseStyle(dark, base, background),
      ...opening(area),
    })
    m.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    m.on('load', () => {
      addAnalystLayers(m, onSelect)
      m.on('click', (e: MapMouseEvent) => {
        if (m.queryRenderedFeatures(e.point, { layers: ['hotspots'] }).length === 0) onSelect(null)
      })
      setReady(1)
    })
    map.current = m

    // El mapa se crea cuando su área aparece, y en ese momento el contenedor todavía no tiene su
    // tamaño final: sin esto el lienzo queda más chico que el hueco y se ve una ventana recortada.
    const observador = new ResizeObserver(() => m.resize())
    observador.observe(container.current)

    return () => {
      observador.disconnect()
      queueMicrotask(() => {
        if (container.current?.isConnected) return
        m.remove()
        map.current = null
      })
    }
  }, [dark, onSelect])

  useEffect(() => {
    const m = map.current
    if (!m || ready === 0) return
    m.setStyle(baseStyle(dark, base, background), { diff: false })
    m.once('styledata', () => {
      addAnalystLayers(m, onSelect)
      setReady((v) => v + 1)
    })
  }, [dark, base.styleUrl, base.darkStyleUrl, base.tileUrl])

  // El fondo de la agencia puede llegar después de abrir el mapa: se repinta solo esa capa.
  useEffect(() => {
    const m = map.current
    if (!m || ready === 0 || !background || !m.getLayer('fondo')) return
    m.setPaintProperty('fondo', 'background-color', background)
  }, [background, ready])

  useEffect(() => {
    const m = map.current
    if (!m || ready === 0) return
    const source = m.getSource<GeoJSONSource>('hotspots')
    if (!source) return

    const puntos = detail?.hotspots ?? []
    void source.setData({
      type: 'FeatureCollection',
      features: puntos.map((h) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [h.longitude, h.latitude] },
        properties: { id: h.hotspotId, risk: h.riskScore, priority: h.priority, selected: h.hotspotId === selected },
      })),
    })

    // Se encuadra una vez por conjunto: después la vista es del analista.
    if (puntos.length > 0 && encuadrado.current !== (detail?.set.setId ?? null)) {
      encuadrado.current = detail?.set.setId ?? null
      const bounds = new LngLatBounds()
      for (const h of puntos) bounds.extend([h.longitude, h.latitude])
      m.fitBounds(bounds, { padding: 60, maxZoom: 13, animate: false })
    }
  }, [detail, selected, ready])

  useEffect(() => {
    const m = map.current
    if (!m || ready === 0) return
    const source = m.getSource<GeoJSONSource>('incidentes')
    if (!source) return

    // Los que no tienen coordenada no se dibujan; se cuentan en la pantalla de calidad, que es
    // donde ese defecto significa algo.
    void source.setData({
      type: 'FeatureCollection',
      features: incidents
        .filter((i) => i.latitude !== null && i.longitude !== null)
        .map((i) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [i.longitude!, i.latitude!] },
          properties: { status: i.status, selected: i.incidentId === selectedIncident },
        })),
    })
  }, [incidents, ready, selectedIncident])

  // Elegir un incidente en la lista lleva el mapa a él (antes la lista no hacía nada al tocarla).
  useEffect(() => {
    const m = map.current
    const i = incidents.find((x) => x.incidentId === selectedIncident)
    if (!m || !i || i.latitude === null || i.longitude === null) return
    m.easeTo({ center: [i.longitude, i.latitude], zoom: Math.max(m.getZoom(), 15), duration: 600 })
  }, [selectedIncident, incidents])

  return <div className="map" ref={container} />
}

export function AnalystScreen({
  lang, dark, sets, detail, onOpen, onCalculate, onPublish, busy, canCalculate, canPublish,
  sources, ingestions, onIngest, quality, incidents, onExplore, changes, onCompare, loaded, problem,
  base = DEFAULT_MAP_BASE, area = null, mapBackground, layers,
}: {
  /** Distritos y zonas (§16.3: «capas»), para quien los carga (H-65). */
  layers?: React.ReactNode
  lang: Lang
  dark: boolean
  /** El fondo de la agencia para el mapa oscuro (§16.3d). */
  mapBackground?: string
  sets: HotspotSet[]
  detail: HotspotSetDetail | null
  onOpen: (setId: string) => void
  onCalculate: (r: { name: string; from: string; to: string; method: string; window: string; top: number }) => void
  onPublish: (setId: string) => void
  busy: boolean
  canCalculate: boolean
  canPublish: boolean
  sources: Source[]
  ingestions: IngestionRun[]
  onIngest: (source: string, since: string, until: string) => void
  quality: QualityReport | null
  incidents: IncidentPage | null
  onExplore: (filter: IncidentFilter) => void
  changes: SetChanges | null
  onCompare: (setId: string | null) => void
  loaded: boolean
  problem: Denial | null
  /** El mapa base configurado y el recuadro de la agencia (§16.2). */
  base?: MapBase
  area?: MapArea | null
}) {
  const ventanas = useAtlasWindows()
  const [source, setSource] = useState('')
  const [ingestSince, setIngestSince] = useState('2025-09-01')
  const [ingestUntil, setIngestUntil] = useState('2026-03-01')
  const corriendo = ingestions.some((r) => r.status === 'RUNNING')
  const [selected, setSelected] = useState<string | null>(null)
  const [incidenteElegido, setIncidenteElegido] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [from, setFrom] = useState('2025-09-01')
  const [to, setTo] = useState('2026-03-01')
  const [method, setMethod] = useState('grid_count')
  const [window, setWindow] = useState('ALL_DAY')
  const [top, setTop] = useState(60)

  const hotspots = useMemo(() => detail?.hotspots ?? [], [detail])

  return (
    <div className="layout">
      <HotspotMap
        detail={detail}
        incidents={incidents?.incidents ?? []}
        selectedIncident={incidenteElegido}
        dark={dark}
        background={mapBackground}
        selected={selected}
        onSelect={setSelected}
        base={base}
        area={area}
      />
      <aside className="side">
        <p className="notice">{translate(lang, 'analyst.help')}</p>

        {/*
          Por etapa del trabajo, no por pantalla: el analista primero mira qué datos tiene,
          después los explora, y al final calcula y publica. Apilar los seis paneles obligaba a
          bajar media pantalla buscando el que uno quería.
        */}
        <Tabs
          area="analyst"
          tabs={[
            {
              id: 'datos',
              label: translate(lang, 'analyst.tabData'),
              content: (
                <>
            {canCalculate && (
              <section className="panel">
                <h2>{translate(lang, 'ingest.title')}</h2>
                <p className="notice">{translate(lang, 'ingest.help')}</p>
                <div className="card">
                  <div className="actions">
                    <select value={source || sources[0]?.key || ''} onChange={(e) => setSource(e.target.value)}
                      aria-label={translate(lang, 'ingest.source')}>
                      {sources.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                    </select>
                    <input type="date" value={ingestSince} onChange={(e) => setIngestSince(e.target.value)}
                      aria-label={translate(lang, 'analyst.from')} />
                    <input type="date" value={ingestUntil} onChange={(e) => setIngestUntil(e.target.value)}
                      aria-label={translate(lang, 'analyst.to')} />
                  </div>
                  <p className="notice">{translate(lang, 'ingest.window')}</p>
                  <button
                    className="primary"
                    disabled={busy || corriendo || sources.length === 0}
                    onClick={() => onIngest(source || sources[0]!.key, ingestSince, ingestUntil)}
                  >
                    {translate(lang, 'ingest.run')}
                  </button>
                  {corriendo && <div className="meta">{translate(lang, 'ingest.running')}</div>}
                </div>

                <DataNote lang={lang} loaded={loaded} problem={problem} count={ingestions.length} empty="ingest.none" />
                {ingestions.slice(0, 6).map((r) => (
                  <div key={r.runId} className={`card ${r.status.startsWith('FAILED') ? 'critical' : ''}`}>
                    <div className="row-line">
                      <strong className="grow">{r.source}</strong>
                      <span className="chip">{translate(lang, `ingestStatus.${r.status.split(':')[0]}`)}</span>
                    </div>
                    {r.error && <p className="notice error">{r.error}</p>}
                    {r.status === 'INTERRUPTED' && <p className="notice warning">{translate(lang, 'ingest.interrupted')}</p>}
                    <div className="meta">{fmtDateTime(r.startedAt, lang)}</div>
                    <div className="meta">
                      {translate(lang, 'ingest.line', [r.rowsWritten, r.rowsRead, r.rowsSkipped])}
                    </div>
                    {r.rowsSkipped > 0 && <div className="meta">{translate(lang, 'ingest.skipped')}</div>}

                    {/* Por qué. Un número de omitidas sin motivo hace que el analista desconfíe
                        de todo el conjunto sin saber de qué: «4 310 sin identificador de origen»
                        es un problema del export, y «2 ilegibles» es otra cosa muy distinta. */}
                    <Desglose lang={lang} titulo="ingest.why" prefijo="skip" cuentas={r.quality.bySkip} />
                    {/* Y qué le falta a lo que sí entró: un incidente sin coordenadas se guarda
                        igual —marcado— porque sirve para contar, pero no alimenta un hotspot. */}
                    <Desglose lang={lang} titulo="ingest.flags" prefijo="flag" cuentas={r.quality.byFlag} />
                  </div>
                ))}
              </section>
            )}

                  <DataQuality lang={lang} report={quality} />
                </>
              ),
            },
            {
              id: 'incidentes',
              label: translate(lang, 'analyst.tabIncidents'),
              content: (
                <IncidentExplorer lang={lang} page={incidents} busy={busy} onSearch={onExplore}
                  selected={incidenteElegido} onSelect={setIncidenteElegido} />
              ),
            },
            ...(layers ? [{
              id: 'capas',
              label: translate(lang, 'analyst.tabLayers'),
              content: layers,
            }] : []),
            {
              id: 'hotspots',
              label: translate(lang, 'analyst.tabHotspots'),
              content: (
                <>
            {canCalculate && (
              <section className="panel">
                <h2>{translate(lang, 'analyst.calculate')}</h2>
                <p className="notice">{translate(lang, 'analyst.calculateHelp')}</p>
                <div className="card">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={translate(lang, 'analyst.name')}
                    style={{ width: '100%' }}
                  />
                  <div className="actions">
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                      aria-label={translate(lang, 'analyst.from')} />
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                      aria-label={translate(lang, 'analyst.to')} />
                  </div>
                  <div className="actions">
                    <select value={method} onChange={(e) => setMethod(e.target.value)}
                      aria-label={translate(lang, 'analyst.method')}>
                      <option value="grid_count">{translate(lang, 'analyst.method.grid_count')}</option>
                      <option value="kde">{translate(lang, 'analyst.method.kde')}</option>
                    </select>
                    <select value={window} onChange={(e) => setWindow(e.target.value)}
                      aria-label={translate(lang, 'analyst.window')}>
                      {ventanas.map((v) => (
                        <option key={v} value={v}>
                          {v === 'ALL_DAY' ? translate(lang, 'analyst.window.ALL_DAY') : v.replace('-', '–')}
                        </option>
                      ))}
                    </select>
                    <input type="number" min={1} max={200} value={top} style={{ width: 70 }}
                      onChange={(e) => setTop(Number(e.target.value))}
                      aria-label={translate(lang, 'analyst.top')} />
                  </div>
                  <p className="notice">{translate(lang, 'analyst.windowWarning')}</p>
                  <button
                    className="primary"
                    disabled={busy || !name.trim()}
                    onClick={() => onCalculate({ name, from, to, method, window, top })}
                  >
                    {translate(lang, 'analyst.run')}
                  </button>
                </div>
              </section>
            )}

            <section className="panel">
              <h2>{translate(lang, 'analyst.sets')} · {sets.length}</h2>
              <DataNote lang={lang} loaded={loaded} problem={problem} count={sets.length} empty="analyst.noSets" />
              {sets.map((s) => (
                <div
                  key={s.setId}
                  className={`card ${detail?.set.setId === s.setId ? 'chosen' : ''}`}
                  onClick={() => onOpen(s.setId)}
                >
                  <div className="row-line">
                    <h3 className="grow">{s.name} · v{s.version}</h3>
                    <span className={`chip ${s.status === 'Published' ? 'ok' : ''}`}>
                      {translate(lang, `setStatus.${s.status}`)}
                    </span>
                  </div>
                  <div className="meta">
                    {s.method} · {s.timeWindow} · {s.hotspotCount} hotspots · {s.inputCount} {translate(lang, 'analyst.incidents')}
                  </div>
                  <div className="meta">{s.periodStart} → {s.periodEnd}</div>
                  {/* Menos de los pedidos no es un fallo: es que no hay más lugares con suficientes
                      incidentes en esa ventana. Sin decirlo, parecía que el cálculo se cortó (H-90). */}
                  {s.requested != null && s.hotspotCount < s.requested && (
                    <div className="meta">{translate(lang, 'analyst.fewer', [s.requested, s.hotspotCount, s.minIncidents ?? 0])}</div>
                  )}
                  <div className="meta">{translate(lang, 'analyst.createdBy', [s.createdBy])}</div>
                  {s.approvedBy && <div className="meta">{translate(lang, 'analyst.publishedBy', [s.approvedBy])}</div>}
                  {/*
                    Comparar va junto a publicar porque es lo que se hace justo antes de firmar:
                    la pregunta «¿en qué cambió respecto del anterior?» decide si este conjunto
                    manda patrullas a otro lado o solo mueve números.
                  */}
                  <div className="actions">
                    {s.version > 1 && (
                      <button
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation()
                          onCompare(changes?.after.setId === s.setId ? null : s.setId)
                        }}
                      >
                        {translate(lang, changes?.after.setId === s.setId ? 'changes.close' : 'changes.open')}
                      </button>
                    )}
                    {canPublish && s.status === 'Candidate' && (
                      <button
                        className="primary"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); onPublish(s.setId) }}
                      >
                        {translate(lang, 'analyst.publish')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </section>

            {changes && <SetChangesPanel lang={lang} changes={changes} />}

            {detail && (
              <section className="panel">
                <h2>{translate(lang, 'analyst.hotspots')} · {hotspots.length}</h2>
                <p className="notice">{translate(lang, 'analyst.fingerprint', [detail.set.fingerprint.slice(0, 16)])}</p>
                {hotspots.slice(0, 40).map((h) => (
                  <div
                    key={h.hotspotId}
                    className={`card ${selected === h.hotspotId ? 'chosen' : ''}`}
                    onClick={() => setSelected(h.hotspotId === selected ? null : h.hotspotId)}
                  >
                    <div className="row-line">
                      <h3 className="grow">#{h.rank} · {h.topOffense || '—'}</h3>
                      <span className="chip">{translate(lang, `priority.${h.priority}`)}</span>
                    </div>
                    <div className="meta">
                      {translate(lang, 'analyst.hotspotLine', [h.incidentCount, Math.round(h.riskScore), h.dwellMinutes])}
                    </div>
                    {h.jurisdiction && <div className="meta">{translate(lang, 'analyst.beat', [h.jurisdiction])}</div>}
                  </div>
                ))}
              </section>
            )}
                </>
              ),
            },
          ]}
        />
      </aside>
    </div>
  )
}

/**
 * Un desglose por tipo de problema, de mayor a menor.
 *
 * Ordenado por cantidad y no alfabéticamente: lo primero que hay que mirar es lo que más pasa.
 * Las claves las manda el servidor como códigos y la frase la pone esta pantalla, porque el
 * servidor no sabe en qué idioma está mirando quien recibe.
 */
function Desglose({
  lang, titulo, prefijo, cuentas,
}: {
  lang: Lang
  titulo: string
  prefijo: 'skip' | 'flag'
  cuentas: Record<string, number>
}) {
  const filas = Object.entries(cuentas).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])
  if (filas.length === 0) return null

  return (
    <div className="meta">
      {translate(lang, titulo)}{' '}
      {filas.map(([codigo, n], i) => (
        <span key={codigo}>
          {i > 0 && ' · '}
          {translate(lang, `${prefijo}.${codigo}`)} {n.toLocaleString(lang === 'es' ? 'es' : 'en')}
        </span>
      ))}
    </div>
  )
}
